import { useCallback, useEffect, useState } from "react";
import { Constants } from "../constants/constants";
import { getOrganizationsAsync } from "../api/OrganizationApi";
import {
  OrganizationListResult,
  OrganizationResponse,
} from "../models/OrganizationModels";

const DEFAULT_PAGE_SIZE = 10;

interface UseOrganizationsReturn {
  organizations: OrganizationResponse[];
  pagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
  loading: boolean;
  error: string | null;
  setPage: (page: number) => void;
  reload: () => Promise<void>;
  removeLocal: (id: string) => void;
  addLocal: (organization: OrganizationResponse) => void;
  updateLocal: (organization: OrganizationResponse) => void;
}

export const useOrganizations = (): UseOrganizationsReturn => {
  const [page, setPage] = useState(1);
  const [data, setData] = useState<OrganizationListResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";

  const load = useCallback(async () => {
    if (!token) {
      setError("Unauthorized");
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const res = await getOrganizationsAsync(
        token,
        page,
        DEFAULT_PAGE_SIZE,
      );
      if (res.success && res.result) {
        setData(res.result);
        setError(null);
      } else {
        setError(res.message || "Failed to load organizations");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load organizations");
    } finally {
      setLoading(false);
    }
  }, [token, page]);

  useEffect(() => {
    load();
  }, [load]);

  const reload = useCallback(async () => {
    setPage(1);
    await load();
  }, [load]);

  const removeLocal = useCallback((id: string) => {
    setData((prev) =>
      prev
        ? {
            ...prev,
            items: prev.items.filter((org) => org.id !== id),
            totalCount: Math.max(0, prev.totalCount - 1),
          }
        : prev,
    );
  }, []);

  const addLocal = useCallback((organization: OrganizationResponse) => {
    setData((prev) =>
      prev
        ? {
            ...prev,
            items: [organization, ...prev.items],
            totalCount: prev.totalCount + 1,
          }
        : prev,
    );
  }, []);

  const updateLocal = useCallback((organization: OrganizationResponse) => {
    setData((prev) =>
      prev
        ? {
            ...prev,
            items: prev.items.map((org) =>
              org.id === organization.id ? organization : org,
            ),
          }
        : prev,
    );
  }, []);

  return {
    organizations: data?.items ?? [],
    pagination: {
      page: data?.page ?? page,
      pageSize: data?.pageSize ?? DEFAULT_PAGE_SIZE,
      totalCount: data?.totalCount ?? 0,
      totalPages: data?.totalPages ?? 1,
      hasNext: data?.hasNext ?? false,
      hasPrevious: data?.hasPrevious ?? false,
    },
    loading,
    error,
    setPage,
    reload,
    removeLocal,
    addLocal,
    updateLocal,
  };
};